import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { PROVIDER_DISPLAY_NAMES, type ProviderType } from '@/data/fleet-types';
import { useSystemCapabilities, providerCanWrite } from '@/hooks/useSystemCapabilities';
import { useReadSettings, useWriteSettings } from '@/hooks/useSystemSettings';
import { useControlTemplates, filterTemplatesByProvider, groupTemplatesByCategory } from '@/hooks/useControlTemplates';
import type { ControlTemplate } from '@/lib/control-templates';
import type { SettingsChange, WriteResult } from '@/lib/control-types';
import { ActionCard, ActionCardSkeleton } from '@/components/control/ActionCard';
import { PreviewDialog } from '@/components/control/PreviewDialog';
import { SettingsReader } from '@/components/control/SettingsReader';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ConfigWizard } from '@/components/config/ConfigWizard';
import {
  Settings2, RefreshCw, Loader2, ChevronDown,
  Zap, SlidersHorizontal, Monitor, Wand2,
  Clock, GitCompareArrows,
} from 'lucide-react';

const SettingsHistory = lazy(() => import('@/components/control/SettingsHistory').then(m => ({ default: m.SettingsHistory })));
const SettingsDiff = lazy(() => import('@/components/control/SettingsDiff').then(m => ({ default: m.SettingsDiff })));
const ConfigDriftCard = lazy(() => import('@/components/control/ConfigDriftCard').then(m => ({ default: m.ConfigDriftCard })));

interface EnergySystem {
  id: string;
  display_name: string | null;
  device_sn: string | null;
  provider_type: string;
  property_id: string;
}

/**
 * Control Panel — installer-only page for reading/writing system settings.
 * Three sections: Quick Actions, Optimization Modes, Advanced Settings.
 */
export default function ControlPanel() {
  const { properties, plants, selectedPropertyId, loading: appLoading } = useApp();

  // System selection state
  const [systems, setSystems] = useState<EnergySystem[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<EnergySystem | null>(null);
  const [loadingSystems, setLoadingSystems] = useState(true);

  // Settings/write state
  const { data: settingsData, loading: readingSettings, error: readError, read: readSettings, clear: clearSettings } = useReadSettings();
  const { result: writeResult, loading: writingSettings, error: writeError, write: writeSettings } = useWriteSettings();

  // DB-backed control templates
  const { templates: allTemplates, isLoading: templatesLoading } = useControlTemplates();

  // Dialog state
  const [previewTemplate, setPreviewTemplate] = useState<ControlTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Lazy-load collapsible sections — don't fetch until expanded
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [diffExpanded, setDiffExpanded] = useState(false);

  // Capabilities: AND provider capability with RBAC permission_role
  const { userProfile } = useApp();
  const providerCaps = useSystemCapabilities(selectedSystem?.provider_type);
  const isAdminOrOwner = ['owner', 'admin'].includes(userProfile?.permission_role ?? '');
  const canReadSettings = providerCaps.canReadSettings && isAdminOrOwner;
  const canWriteSettings = providerCaps.canWriteSettings && isAdminOrOwner;

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);

  // Fetch systems for selected property
  useEffect(() => {
    clearSettings();
    setSelectedSystem(null);
    setSystems([]);

    if (!selectedPropertyId) {
      setLoadingSystems(false);
      return;
    }

    setLoadingSystems(true);
    supabase
      .from('energy_systems')
      .select('id, display_name, device_sn, provider_type, property_id')
      .eq('property_id', selectedPropertyId)
      .then(({ data, error }) => {
        if (error) {
          console.error('[ControlPanel] Failed to load systems:', error);
          setLoadingSystems(false);
          return;
        }
        const systemsList = (data ?? []) as EnergySystem[];
        setSystems(systemsList);
        // Auto-select the first system with write capability, or first system
        const writable = systemsList.find(s => providerCanWrite(s.provider_type));
        setSelectedSystem(writable ?? systemsList[0] ?? null);
        setLoadingSystems(false);
      });
  }, [selectedPropertyId, clearSettings]);

  // Fetch settings when system changes
  const handleReadSettings = useCallback(() => {
    if (!selectedSystem?.id || !selectedSystem.device_sn) return;
    readSettings(selectedSystem.id, selectedSystem.device_sn, true);
  }, [selectedSystem, readSettings]);

  // Handle template action
  const handleActionClick = (template: ControlTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  // Execute write
  const handleConfirmWrite = async (changes: SettingsChange[]): Promise<WriteResult | null> => {
    if (!selectedSystem?.id || !selectedSystem.device_sn) return null;
    const result = await writeSettings(selectedSystem.id, selectedSystem.device_sn, changes);
    // Refresh settings after write
    if (result?.success) {
      setTimeout(() => {
        readSettings(selectedSystem.id, selectedSystem.device_sn, true);
      }, 1000);
    }
    return result;
  };

  // Template grouping — DB-backed with provider filtering
  const providerType = selectedSystem?.provider_type ?? '';
  const { active: providerTemplates, comingSoon } = filterTemplatesByProvider(allTemplates, providerType);
  const grouped = groupTemplatesByCategory(providerTemplates);
  const quickActions = grouped['quick_action'] ?? [];
  const optimizations = grouped['optimization'] ?? [];

  // --- Render ---

  if (appLoading) {
    return <ControlPanelSkeleton />;
  }

  if (!selectedPropertyId || !selectedProperty) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-8 text-center">
          <Monitor className="h-8 w-8 text-wt-text-disabled mx-auto mb-3" />
          <p className="text-sm text-wt-text-muted">Select a property to access the Control Panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-wt-text-primary">Control Panel</h1>
          <p className="text-sm text-wt-text-muted mt-0.5">
            {selectedProperty.name}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* System selector (if multiple) */}
          {systems.length > 1 && (
            <select
              value={selectedSystem?.id ?? ''}
              onChange={(e) => {
                const sys = systems.find(s => s.id === e.target.value);
                setSelectedSystem(sys ?? null);
                clearSettings();
              }}
              className="text-xs rounded-md border border-wt-border-default bg-wt-bg-primary text-wt-text-primary px-2 py-1.5"
            >
              {systems.map(s => (
                <option key={s.id} value={s.id}>
                  {s.display_name ?? s.device_sn ?? s.id}
                </option>
              ))}
            </select>
          )}
          {/* Provider badge */}
          {selectedSystem && (
            <ProviderBadge provider={selectedSystem.provider_type} />
          )}
          {/* Write capability badge */}
          {selectedSystem && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs border',
                canWriteSettings
                  ? 'border-wt-green-primary/30 text-wt-green-primary bg-wt-green-primary/5'
                  : 'border-wt-border-subtle text-wt-text-disabled',
              )}
            >
              {canWriteSettings ? 'Write enabled' : 'Read only'}
            </Badge>
          )}
          {/* Guided Setup button — only for writable systems with admin/owner */}
          {canWriteSettings && selectedSystem && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWizard(true)}
              className="border-wt-border-default text-wt-text-secondary text-xs"
            >
              <Wand2 className="h-3 w-3 mr-1.5" />
              Guided Setup
            </Button>
          )}
        </div>
      </div>

      {/* Loading systems */}
      {loadingSystems && <ControlPanelSkeleton />}

      {/* No systems */}
      {!loadingSystems && systems.length === 0 && (
        <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-8 text-center">
          <Settings2 className="h-8 w-8 text-wt-text-disabled mx-auto mb-3" />
          <p className="text-sm text-wt-text-muted">No systems found for this property.</p>
        </div>
      )}

      {/* No device serial */}
      {selectedSystem && !selectedSystem.device_sn && (
        <div className="rounded-lg border border-wt-amber-primary/20 bg-wt-amber-primary/5 p-4">
          <p className="text-sm text-wt-amber-primary">
            This system has no device serial number configured. Settings read/write requires a device serial.
          </p>
        </div>
      )}

      {/* Main content — only when system selected with device_sn */}
      {selectedSystem && selectedSystem.device_sn && !loadingSystems && (
        <>
          {/* Section 1: Quick Actions */}
          {canWriteSettings && quickActions.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-wt-text-secondary" />
                <h2 className="text-sm font-medium text-wt-text-primary">Quick Actions</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {quickActions.map(template => (
                  <ActionCard
                    key={template.id}
                    template={template}
                    disabled={!canWriteSettings}
                    onClick={handleActionClick}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Section 2: Optimization Modes */}
          {canWriteSettings && optimizations.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <SlidersHorizontal className="h-4 w-4 text-wt-text-secondary" />
                <h2 className="text-sm font-medium text-wt-text-primary">Optimization Modes</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {optimizations.map(template => (
                  <ActionCard
                    key={template.id}
                    template={template}
                    disabled={!canWriteSettings}
                    onClick={handleActionClick}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Coming Soon section */}
          {canWriteSettings && comingSoon.length > 0 && (
            <section className="opacity-60">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-sm font-medium text-wt-text-disabled">Coming Soon</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {comingSoon.map(template => (
                  <ActionCard
                    key={template.id}
                    template={template}
                    disabled
                    onClick={() => {}}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Permission notice for viewers */}
          {!isAdminOrOwner && (
            <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-4">
              <p className="text-sm text-wt-text-muted">
                You have viewer-level access. Settings read/write requires admin or owner permissions.
              </p>
            </div>
          )}

          {/* Read-only notice for non-writable providers (only show when user has permission) */}
          {isAdminOrOwner && !providerCaps.canWriteSettings && (
            <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-4">
              <p className="text-sm text-wt-text-muted">
                {PROVIDER_DISPLAY_NAMES[selectedSystem.provider_type as ProviderType] ?? selectedSystem.provider_type} systems
                do not currently support remote settings changes.
                {providerCaps.canReadSettings ? ' You can read the current configuration below.' : ''}
              </p>
            </div>
          )}

          {/* Section 3: Advanced Settings */}
          {canReadSettings && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-wt-text-secondary" />
                  <h2 className="text-sm font-medium text-wt-text-primary">Current Settings</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReadSettings}
                  disabled={readingSettings}
                  className="border-wt-border-default text-wt-text-secondary text-xs"
                >
                  {readingSettings ? (
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1.5" />
                  )}
                  {settingsData ? 'Refresh' : 'Read Settings'}
                </Button>
              </div>
              <SettingsReader
                categorized={settingsData?.categorized ?? null}
                loading={readingSettings}
                error={readError}
                readAt={settingsData?.read_at ?? null}
                onRefresh={handleReadSettings}
              />
            </section>
          )}

          {/* Section 4: Settings History (lazy-loaded) */}
          {canReadSettings && (
            <section>
              <button
                type="button"
                onClick={() => setHistoryExpanded(prev => !prev)}
                className="w-full flex items-center gap-2 py-2 text-left group"
              >
                <ChevronDown className={cn('h-4 w-4 text-wt-text-muted transition-transform', !historyExpanded && '-rotate-90')} />
                <Clock className="h-4 w-4 text-wt-text-secondary" />
                <h2 className="text-sm font-medium text-wt-text-primary">Settings History</h2>
              </button>
              {historyExpanded && (
                <Suspense fallback={<Skeleton className="h-24 w-full rounded-lg" />}>
                  <SettingsHistory systemId={selectedSystem.id} />
                </Suspense>
              )}
            </section>
          )}

          {/* Section 5: Config Drift Alerts */}
          {selectedSystem && (
            <Suspense fallback={null}>
              <ConfigDriftCard systemId={selectedSystem.id} />
            </Suspense>
          )}

          {/* Section 6: Compare to Baseline (lazy-loaded) */}
          {canReadSettings && (
            <section>
              <button
                type="button"
                onClick={() => setDiffExpanded(prev => !prev)}
                className="w-full flex items-center gap-2 py-2 text-left group"
              >
                <ChevronDown className={cn('h-4 w-4 text-wt-text-muted transition-transform', !diffExpanded && '-rotate-90')} />
                <GitCompareArrows className="h-4 w-4 text-wt-text-secondary" />
                <h2 className="text-sm font-medium text-wt-text-primary">Compare to Baseline</h2>
              </button>
              {diffExpanded && (
                <Suspense fallback={<Skeleton className="h-24 w-full rounded-lg" />}>
                  <SettingsDiff systemId={selectedSystem.id} />
                </Suspense>
              )}
            </section>
          )}
        </>
      )}

      {/* Preview/Confirm Dialog */}
      <PreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        template={previewTemplate}
        currentSettings={settingsData?.settings ?? null}
        onConfirm={handleConfirmWrite}
        writing={writingSettings}
      />

      {/* Config Wizard Dialog */}
      {selectedSystem && (
        <Dialog open={showWizard} onOpenChange={setShowWizard}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">Inverter Configuration Wizard</DialogTitle>
            <ConfigWizard
              systemId={selectedSystem.id}
              onComplete={() => {
                setShowWizard(false);
                if (selectedSystem.device_sn) {
                  readSettings(selectedSystem.id, selectedSystem.device_sn, true);
                }
              }}
              onCancel={() => setShowWizard(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ControlPanelSkeleton() {
  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      <div className="space-y-2">
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="h-4 w-64 rounded" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-28 rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <ActionCardSkeleton key={i} />)}
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-36 rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <ActionCardSkeleton key={i} />)}
        </div>
      </div>
    </div>
  );
}
